import type { DefaultIndent, IndentEntity, LegacyIndent } from './schemas';
import { createIndentStylesLegacy } from './schemas';

export type CreateIndentOptions = {
    /**
     * Desktop height in px. Also seeds `mobileStyles` unless `mobileHeight` says
     * otherwise — the FormGroup editor did exactly this when it met a widget with
     * no persisted mobile height, and the host's backfill path relies on it to
     * avoid dropping an old spacer onto the package default.
     */
    height?: number;
    mobileHeight?: number;
};

/**
 * Factory for the current legacy indent entity — matches the persisted widget JSON
 * shape: `{ id, name: 'indent', columnId, data, styles, mobileStyles }`. No variant
 * argument: the spacer has a single placement (never zero-block).
 */
export function createLegacyIndent(options?: CreateIndentOptions): LegacyIndent {
    const styles = createIndentStylesLegacy(options?.height != null ? { height: options.height } : {});

    return {
        id: '',
        name: 'indent',
        columnId: '',
        data: {
            isSynchronizeStyle: true,
        },
        styles,
        mobileStyles: createIndentStylesLegacy({ height: options?.mobileHeight ?? styles.height }),
    };
}

/**
 * Factory for a fresh spacer entity — the same seed as {@link createLegacyIndent}
 * (40px on both devices, synchronised). Identity and the parent link are the
 * host's to fill in.
 */
export function createDefaultIndent(): DefaultIndent {
    const { height } = createIndentStylesLegacy();

    return {
        id: '',
        parentId: '',
        entityType: 'widget:indent',
        entitySubType: 'default',
        version: 0,
        meta: {
            displayId: null,
            createdAt: Date.now(),
        },
        props: {
            syncWithDesktop: true,
            height: { desktop: height, mobile: height },
        },
    };
}

/**
 * Convenience entry-point that returns whichever shape is the current default for
 * new indent widgets — the versioned entity, via {@link createDefaultIndent}.
 */
export function createIndent(): IndentEntity {
    return createDefaultIndent();
}
